import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BlockMath } from "react-katex";
import { FigureRender } from "../components/FigureRender";

const difficultyOptions = ["all", "LOT", "MOT", "HOT"];
const statusOptions = ["all", "not_started", "mastered", "developing", "needs_focus"];
const sortOptions = [
  { value: "difficulty", label: "Difficulty (LOT → HOT)" },
  { value: "frameOrder", label: "Frame Order" },
  { value: "masteryAsc", label: "Mastery (Lowest first)" },
  { value: "masteryDesc", label: "Mastery (Highest first)" },
  { value: "recent", label: "Most Recent" },
  { value: "oldest", label: "Oldest" }
];

const difficultyRank = { LOT: 1, MOT: 2, HOT: 3 };

function formatAttemptDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

function getStatusLabel(status) {
  if (status === "not_started") return "Not Started";
  if (status === "mastered") return "Mastered";
  if (status === "developing") return "Developing";
  if (status === "needs_focus") return "Needs Focus";
  return "All";
}

function getMasteryClass(status) {
  if (status === "not_started") return "not-started";
  if (status === "mastered") return "mastered";
  if (status === "developing") return "developing";
  return "needs-focus";
}

function getDifficultyClass(difficulty) {
  const diff = String(difficulty || "").toUpperCase();
  if (diff === "HOT") return "diff-hot";
  if (diff === "MOT") return "diff-mot";
  return "diff-lot";
}

function ChapterHome({ apiBase, token, onPageBusyChange }) {
  const { chapterId } = useParams();
  const [chapterTitle, setChapterTitle] = useState(chapterId);
  const [difficulty, setDifficulty] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("difficulty");
  const [allFrames, setAllFrames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedFrameIds, setExpandedFrameIds] = useState(() => new Set());

  useEffect(() => {
    if (typeof onPageBusyChange === "function") {
      onPageBusyChange("chapter-home", loading, "Loading chapter frames...");
    }
    return () => {
      if (typeof onPageBusyChange === "function") {
        onPageBusyChange("chapter-home", false);
      }
    };
  }, [loading, onPageBusyChange]);

  useEffect(() => {
    let mounted = true;

    async function loadChapters() {
      if (!token) return;
      try {
        const response = await fetch(`${apiBase}/api/chapters`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) return;
        const payload = await response.json();
        const found = (payload.chapters || []).find((item) => item.id === chapterId);
        if (mounted && found?.title) setChapterTitle(found.title);
      } catch (err) {
        // ignore
      }
    }

    loadChapters();
    return () => {
      mounted = false;
    };
  }, [apiBase, token, chapterId]);

  useEffect(() => {
    let mounted = true;

    async function loadFrames() {
      if (!token) return;
      setLoading(true);
      setError("");
      try {
        const response = await fetch(`${apiBase}/api/mastery/chapters/${chapterId}/frames`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error || "Failed to load frame summaries");
        }
        const payload = await response.json();
        if (mounted) setAllFrames(payload.frames || []);
      } catch (err) {
        if (mounted) setError(err.message || "Failed to load frame summaries");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadFrames();
    return () => {
      mounted = false;
    };
  }, [apiBase, token, chapterId]);

  // Apply filters + sort client-side
  const frames = useMemo(() => {
    let result = [...allFrames];

    if (difficulty !== "all") {
      result = result.filter((f) => f.difficulty === difficulty);
    }

    if (statusFilter !== "all") {
      result = result.filter((f) => (f.masteryStatus || "not_started") === statusFilter);
    }

    if (sortBy === "difficulty") {
      result.sort((a, b) => {
        const da = difficultyRank[a.difficulty] || 2;
        const db = difficultyRank[b.difficulty] || 2;
        if (da !== db) return da - db;
        const oa = Number.isFinite(Number(a.frameOrder)) ? Number(a.frameOrder) : Number.MAX_SAFE_INTEGER;
        const ob = Number.isFinite(Number(b.frameOrder)) ? Number(b.frameOrder) : Number.MAX_SAFE_INTEGER;
        return oa - ob;
      });
    } else if (sortBy === "frameOrder") {
      result.sort((a, b) => {
        const oa = Number.isFinite(Number(a.frameOrder)) ? Number(a.frameOrder) : Number.MAX_SAFE_INTEGER;
        const ob = Number.isFinite(Number(b.frameOrder)) ? Number(b.frameOrder) : Number.MAX_SAFE_INTEGER;
        return oa - ob;
      });
    } else if (sortBy === "masteryAsc") {
      result.sort((a, b) => (Number(a.masteryScore) || 0) - (Number(b.masteryScore) || 0));
    } else if (sortBy === "masteryDesc") {
      result.sort((a, b) => (Number(b.masteryScore) || 0) - (Number(a.masteryScore) || 0));
    } else if (sortBy === "oldest") {
      result.sort((a, b) => {
        const at = a.latestAttemptAt ? new Date(a.latestAttemptAt).getTime() : 0;
        const bt = b.latestAttemptAt ? new Date(b.latestAttemptAt).getTime() : 0;
        return at - bt;
      });
    } else {
      // recent
      result.sort((a, b) => {
        const at = a.latestAttemptAt ? new Date(a.latestAttemptAt).getTime() : 0;
        const bt = b.latestAttemptAt ? new Date(b.latestAttemptAt).getTime() : 0;
        return bt - at;
      });
    }

    return result;
  }, [allFrames, difficulty, statusFilter, sortBy]);

  const toggleFrameExpand = (frameId) => {
    setExpandedFrameIds((prev) => {
      const next = new Set(prev);
      if (next.has(frameId)) {
        next.delete(frameId);
      } else {
        next.add(frameId);
      }
      return next;
    });
  };

  const handleExpandAll = () => {
    setExpandedFrameIds(new Set(frames.map((f) => f.frameId)));
  };

  const handleCollapseAll = () => {
    setExpandedFrameIds(new Set());
  };

  const allAreExpanded = frames.length > 0 && expandedFrameIds.size >= frames.length;

  return (
    <section className="page chapter-home-page">
      <div className="chapter-home-header">
        <div>
          <div className="breadcrumb">Home / {chapterTitle}</div>
          <h2 className="page-title">{chapterTitle}</h2>
        </div>
      </div>

      <div className="filters-panel">
        <div className="filter-group">
          <div className="filter-label">Difficulty</div>
          <div className="filter-row">
            {difficultyOptions.map((item) => (
              <button
                key={item}
                type="button"
                className={`filter-pill ${difficulty === item ? "active" : ""}`}
                onClick={() => setDifficulty(item)}
              >
                {item === "all" ? "All" : item}
              </button>
            ))}
          </div>
        </div>
        <div className="filter-group">
          <div className="filter-label">Status</div>
          <div className="filter-row">
            {statusOptions.map((item) => (
              <button
                key={item}
                type="button"
                className={`filter-pill ${statusFilter === item ? "active" : ""}`}
                onClick={() => setStatusFilter(item)}
              >
                {item === "all" ? "All" : getStatusLabel(item)}
              </button>
            ))}
          </div>
        </div>
        <div className="filter-group align-end">
          <div className="filter-label">Sort</div>
          <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="question-block">
        <div className="question-title-row">
          <div className="question-meta">Question Frames ({frames.length})</div>
          {frames.length > 0 && (
            <div className="frame-list-controls">
              <button
                type="button"
                className="frame-bulk-toggle-btn"
                onClick={allAreExpanded ? handleCollapseAll : handleExpandAll}
              >
                {allAreExpanded ? "Collapse All" : "Expand All"}
              </button>
            </div>
          )}
        </div>

        {loading && <div className="question-meta">Loading frame summaries...</div>}
        {error && <div className="error-text">{error}</div>}
        {!loading && !error && frames.length === 0 && (
          <div className="question-meta">No frames found for this chapter yet.</div>
        )}

        <div className="frame-list">
          {frames.map((frame, index) => {
            const isExpanded = expandedFrameIds.has(frame.frameId);
            const difficultyClass = getDifficultyClass(frame.difficulty);
            const practiceUrl = `/practice/${chapterId}?frameOrder=${frame.frameOrder}&difficulty=${frame.difficulty}`;
            const attemptsUrl = `/chapters/${chapterId}/frames/${frame.frameId}`;

            return (
              <div
                key={frame.frameId}
                className={`frame-horizontal-card ${isExpanded ? "is-expanded" : ""}`}
              >
                {/* Collapsed Mode / Card Bar */}
                <div
                  className="frame-card-bar"
                  onClick={() => toggleFrameExpand(frame.frameId)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleFrameExpand(frame.frameId);
                    }
                  }}
                >
                  <div className="frame-bar-left">
                    <span className="frame-circle-num">{index + 1}</span>
                    <div className="frame-preview-content">
                      {frame.questionLatex ? (
                        <div className="frame-preview-math">
                          <BlockMath
                            math={frame.questionLatex}
                            renderError={() => (
                              <span className="question-fallback-text">{frame.questionLatex}</span>
                            )}
                          />
                        </div>
                      ) : (
                        <span className="question-empty-text">{frame.frameTitle || "Untitled Question"}</span>
                      )}
                    </div>
                  </div>

                  <div className="frame-bar-right" onClick={(e) => e.stopPropagation()}>
                    <div className="frame-badges-cluster">
                      <span className={`frame-diff-pill ${difficultyClass}`}>
                        {frame.difficulty}
                      </span>
                      <span className={`frame-status-badge ${getMasteryClass(frame.masteryStatus)}`}>
                        {frame.masteryLabel || getStatusLabel(frame.masteryStatus)}
                      </span>
                      <span className="frame-attempts-pill">
                        {frame.attemptsCount || 0} {frame.attemptsCount === 1 ? "attempt" : "attempts"}
                      </span>
                    </div>

                    <div className="frame-bar-actions">
                      <Link
                        to={practiceUrl}
                        className="frame-quick-practice-btn"
                        title={`Practice Frame ${frame.frameOrder} (${frame.difficulty})`}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                          <polygon points="5 3 19 12 5 21 5 3"></polygon>
                        </svg>
                        <span>Practice</span>
                      </Link>

                      <button
                        type="button"
                        className="frame-chevron-btn"
                        onClick={() => toggleFrameExpand(frame.frameId)}
                        aria-label={isExpanded ? "Collapse frame details" : "Expand frame details"}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className={`chevron-icon ${isExpanded ? "rotated" : ""}`}
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Mode Drawer */}
                {isExpanded && (
                  <div className="frame-expanded-drawer">
                    <div className="frame-drawer-top">
                      <div className="frame-drawer-title-row">
                        <h4 className="frame-drawer-title">{frame.frameTitle || "Untitled Frame"}</h4>
                        {frame.latestAttemptAt && (
                          <span className="frame-drawer-date">
                            Last practiced: {formatAttemptDate(frame.latestAttemptAt)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="frame-drawer-question-section">
                      <div className="frame-drawer-section-label">Instantiated Question</div>
                      <div className="frame-drawer-math-wrap">
                        {frame.questionLatex ? (
                          <BlockMath
                            math={frame.questionLatex}
                            renderError={() => (
                              <span className="question-fallback-text">{frame.questionLatex}</span>
                            )}
                          />
                        ) : (
                          <span className="question-empty-text">No question text</span>
                        )}
                      </div>

                      {frame.figureSpec && (
                        <div className="frame-drawer-figure">
                          <FigureRender
                            figureSpec={frame.figureSpec}
                            variables={frame.defaultVariables || {}}
                            cropToContent
                            contentPadding={8}
                            theme="accepted"
                          />
                        </div>
                      )}
                    </div>

                    <div className="frame-drawer-metrics-section">
                      <div className="frame-drawer-section-label">Performance & Mastery</div>
                      <div className="frame-metrics-grid">
                        <div className="metric-tile metric-tile-mastery">
                          <div className="metric-tile-label">Mastery Score</div>
                          <div className="metric-tile-val">
                            {Number.isFinite(Number(frame.masteryScore)) ? Number(frame.masteryScore) : 0}%
                          </div>
                          <div className="mastery-mini-progress">
                            <div
                              className="mastery-mini-progress-fill"
                              style={{
                                width: `${Math.min(100, Math.max(0, Number(frame.masteryScore) || 0))}%`
                              }}
                            />
                          </div>
                        </div>

                        <div className="metric-tile">
                          <div className="metric-tile-label">Attempts</div>
                          <div className="metric-tile-val">{frame.attemptsCount || 0}</div>
                        </div>

                        <div className="metric-tile">
                          <div className="metric-tile-label">Correct</div>
                          <div className="metric-tile-val metric-green">
                            {frame.statusCounts?.correct || 0}
                          </div>
                        </div>

                        <div className="metric-tile">
                          <div className="metric-tile-label">Incomplete</div>
                          <div className="metric-tile-val metric-amber">
                            {frame.statusCounts?.incomplete || 0}
                          </div>
                        </div>

                        <div className="metric-tile">
                          <div className="metric-tile-label">Review Needed</div>
                          <div className="metric-tile-val metric-blue">
                            {frame.statusCounts?.needs_review || 0}
                          </div>
                        </div>

                        <div className="metric-tile">
                          <div className="metric-tile-label">Needs Improvement</div>
                          <div className="metric-tile-val metric-red">
                            {frame.statusCounts?.needs_improvement || 0}
                          </div>
                        </div>
                      </div>
                    </div>

                    {((frame.focusTags && frame.focusTags.length > 0) ||
                      (frame.strengthTags && frame.strengthTags.length > 0) ||
                      (frame.tags && frame.tags.length > 0)) && (
                      <div className="frame-drawer-tags-section">
                        {frame.focusTags?.length > 0 && (
                          <div className="frame-tags-group">
                            <span className="tags-label">Focus Areas:</span>
                            {frame.focusTags.map((tag, i) => (
                              <span key={i} className="frame-tag-pill focus-pill">{tag}</span>
                            ))}
                          </div>
                        )}
                        {frame.strengthTags?.length > 0 && (
                          <div className="frame-tags-group">
                            <span className="tags-label">Strengths:</span>
                            {frame.strengthTags.map((tag, i) => (
                              <span key={i} className="frame-tag-pill strength-pill">{tag}</span>
                            ))}
                          </div>
                        )}
                        {(!frame.focusTags?.length && !frame.strengthTags?.length && frame.tags?.length > 0) && (
                          <div className="frame-tags-group">
                            <span className="tags-label">Topics:</span>
                            {frame.tags.map((tag, i) => (
                              <span key={i} className="frame-tag-pill topic-pill">{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="frame-drawer-actions">
                      <Link
                        to={practiceUrl}
                        className="frame-cta-practice-slate"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                          <polygon points="5 3 19 12 5 21 5 3"></polygon>
                        </svg>
                        <span>Practice on Slate</span>
                      </Link>

                      <Link
                        to={attemptsUrl}
                        className="frame-cta-view-attempts"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        <span>View Past Attempts ({frame.attemptsCount || 0})</span>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default ChapterHome;
